import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '../../design-system';
import { resolveTenant, TenantPublicInfo } from '../../services/consentApi';
import { 
  getWithdrawalHistory,
  getPortalToken,
  getPortalUser,
  isPortalSessionValid,
  clearPortalSession,
  WithdrawalHistoryItem 
} from '../../services/portalApi';
import { useI18n } from '../../i18n';
import '../../design-system/styles.css';

/**
 * Data Owner Withdrawal History Page
 * Route: /t/:tenantSlug/my-consent/withdrawals
 */
const DataOwnerWithdrawalHistory: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const { t, language } = useI18n();

  // State
  const [tenant, setTenant] = useState<TenantPublicInfo | null>(null);
  const [withdrawals, setWithdrawals] = useState<WithdrawalHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check session and load data
  useEffect(() => {
    const loadData = async () => {
      if (!isPortalSessionValid()) {
        clearPortalSession();
        navigate(`/t/${tenantSlug}/my-consent/login`);
        return;
      }

      const token = getPortalToken();
      const user = getPortalUser();
      if (!token || !user) {
        navigate(`/t/${tenantSlug}/my-consent/login`);
        return;
      }

      try {
        const [tenantInfo, withdrawalData] = await Promise.all([
          resolveTenant(tenantSlug!),
          getWithdrawalHistory(token, user.tenantId, user.dataOwnerId),
        ]);
        
        setTenant(tenantInfo);
        setWithdrawals(withdrawalData);
      } catch (err: any) {
        if (err.response?.status === 401) {
          clearPortalSession();
          navigate(`/t/${tenantSlug}/my-consent/login`);
          return;
        }
        setError(err.response?.data?.message || 'Failed to load withdrawal history');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantSlug, navigate]);

  // Get status badge style
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'PENDING':
        return { background: 'var(--ds-warning-light)', color: 'var(--ds-warning)' };
      case 'APPROVED':
        return { background: 'var(--ds-success-light)', color: 'var(--ds-success)' };
      case 'REJECTED':
        return { background: 'var(--ds-error-light)', color: 'var(--ds-error)' };
      default:
        return { background: 'var(--ds-bg-secondary)', color: 'var(--ds-text-muted)' };
    }
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return t.portal?.statusPending || 'Pending';
      case 'APPROVED':
        return t.portal?.statusApproved || 'Approved';
      case 'REJECTED':
        return t.portal?.statusRejected || 'Rejected';
      default:
        return status;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="ds-container" style={{ textAlign: 'center', paddingTop: 100 }}>
        <p className="ds-text-muted">{t.common.loading}</p>
      </main>
    );
  }

  // User info
  const user = getPortalUser();

  // Stats calculation
  const stats = {
    pending: withdrawals.filter(w => w.status === 'PENDING' || w.status === 'PROCESSING').length,
    approved: withdrawals.filter(w => w.status === 'APPROVED').length,
    rejected: withdrawals.filter(w => w.status === 'REJECTED').length,
  };

  return (
    <main style={{ minHeight: '100vh', background: 'var(--ds-bg-secondary)' }} role="main" aria-labelledby="page-title">
      {/* Skip to main content link */}
      <a 
        href="#main-content" 
        className="sr-only"
        style={{ position: 'absolute' }}
        onFocus={(e) => {
          e.currentTarget.classList.remove('sr-only');
          e.currentTarget.style.position = 'fixed';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.left = '10px';
          e.currentTarget.style.padding = '12px 24px';
          e.currentTarget.style.background = 'var(--tashi-blue-grey-9)';
          e.currentTarget.style.color = 'white';
          e.currentTarget.style.borderRadius = '8px';
          e.currentTarget.style.zIndex = '9999';
          e.currentTarget.style.fontWeight = '600';
        }}
        onBlur={(e) => {
          e.currentTarget.classList.add('sr-only');
        }}
      >
        Skip to main content
      </a>

      {/* Header */}
      <header style={{ 
        background: 'linear-gradient(135deg, var(--tashi-blue-grey-9) 0%, var(--tashi-blue-grey-7) 100%)', 
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div style={{ 
          maxWidth: 1200, 
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <button
            onClick={() => navigate(`/t/${tenantSlug}/my-consent/history`)}
            aria-label={t.common?.back || 'Back to consent history'}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '2px solid transparent',
              fontSize: 18,
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: 8,
              color: 'white',
            }}
          >
            <span aria-hidden="true">←</span>
          </button>
          <div style={{ flex: 1 }}>
            <h1 id="page-title" style={{ fontSize: 18, margin: 0, fontWeight: 600, color: 'white' }}>
              {t.portal?.withdrawalHistory || 'Withdrawal Requests'}
            </h1>
            {tenant && (
              <p style={{ fontSize: 14, margin: 0, color: 'rgba(255,255,255,0.9)' }}>
                {tenant.name}
              </p>
            )}
          </div>
          {user && (
            <div aria-hidden="true" style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 600,
              fontSize: 14,
            }}>
              {user.firstName?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </header>

      <div id="main-content" style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        {/* Stats summary */}
        {withdrawals.length > 0 && (
          <Card style={{ 
            marginBottom: 24, 
            padding: 20,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          }}>
            <section aria-label="Withdrawal statistics" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: 12,
            }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ds-warning)' }}>
                  {stats.pending}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {t.portal?.statusPending || 'Pending'}
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ds-success)' }}>
                  {stats.approved}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {t.portal?.statusApproved || 'Approved'}
                </p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--ds-error)' }}>
                  {stats.rejected}
                </p>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                  {t.portal?.statusRejected || 'Rejected'}
                </p>
              </div>
            </section>
          </Card>
        )}

        {/* Error message - aria-live for screen readers */}
        <div aria-live="polite" aria-atomic="true">
          {error && (
            <div className="ds-alert ds-alert--error" role="alert" style={{ marginBottom: 16 }}>
              {error}
            </div>
          )}
        </div>

        {/* Withdrawal list */}
        {withdrawals.length === 0 ? (
          <Card style={{ padding: 32, textAlign: 'center' }}>
            <span aria-hidden="true" style={{ fontSize: 48, display: 'block', marginBottom: 16 }}>📝</span>
            <p style={{ margin: 0, color: 'var(--ds-text-muted)', fontSize: 16 }}>
              {t.portal?.noWithdrawals || 'No withdrawal requests yet'}
            </p>
            <Link 
              to={`/t/${tenantSlug}/my-consent/history`}
              style={{ 
                display: 'inline-block',
                marginTop: 16,
                color: 'var(--ds-accent)',
                fontSize: 14,
              }}
            >
              {t.portal?.viewConsents || 'View your consents'}
            </Link>
          </Card>
        ) : (
          <section aria-label="Withdrawal requests">
            <h2 className="sr-only">Withdrawal Requests</h2>
            <ul style={{ 
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', 
              gap: 16 
            }}>
              {withdrawals.map((withdrawal) => (
                <li key={withdrawal.withdrawalId}>
                  <Card style={{ padding: 20, height: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
                          {withdrawal.projectName || withdrawal.templateName}
                        </h3>
                        <p style={{ margin: 0, fontSize: 14, color: 'var(--ds-text-muted)' }}>
                          {t.portal?.requestedOn || 'Requested on'}: {withdrawal.requestedAt ? formatDate(withdrawal.requestedAt) : 'N/A'}
                        </p>
                      </div>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 14,
                        fontWeight: 600,
                        ...getStatusStyle(withdrawal.status),
                      }}>
                        {getStatusLabel(withdrawal.status)}
                      </span>
                    </div>

                    {withdrawal.reason && (
                      <p style={{ 
                        margin: '8px 0 0', 
                        fontSize: 14, 
                        color: 'var(--ds-text-secondary)',
                        fontStyle: 'italic',
                      }}>
                        "{withdrawal.reason}"
                      </p>
                    )}

                    {withdrawal.processedAt && (
                      <p style={{ 
                        margin: '8px 0 0', 
                        fontSize: 14, 
                        color: 'var(--ds-text-muted)' 
                      }}>
                        {t.portal?.processedOn || 'Processed on'}: {formatDate(withdrawal.processedAt)}
                      </p>
                    )}

                    {withdrawal.rejectionReason && (
                      <p style={{ 
                        margin: '8px 0 0', 
                        fontSize: 14, 
                        color: 'var(--ds-error)' 
                      }}>
                        {'Reason'}: {withdrawal.rejectionReason}
                      </p>
                    )}

                    {(withdrawal.status === 'PENDING' || withdrawal.status === 'PROCESSING') && (
                      <div style={{ 
                        marginTop: 12, 
                        padding: 12, 
                        background: 'var(--ds-bg-secondary)',
                        borderRadius: 8,
                        fontSize: 14,
                      }}>
                        <span aria-hidden="true">⏳</span> {t.portal?.processingMessage || 'Your request is being processed. You will be notified once completed.'}
                      </div>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Info card */}
        <Card style={{ marginTop: 16, padding: 16, background: 'var(--ds-bg-secondary)' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>
            {t.portal?.processingTimeInfo || 'Processing Time Information'}
          </h2>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.8, color: 'var(--ds-text-secondary)' }}>
            <li>DPDP Act (India): 7 days</li>
            <li>GDPR (EU): 30 days</li>
            <li>CCPA (California): 45 days</li>
          </ul>
        </Card>
      </div>
    </main>
  );
};

export default DataOwnerWithdrawalHistory;
